// src/pages/Accounting/Reconcile/Session/TableSettings.tsx

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { PALETTE } from "../constants";
import type { ColumnKey, Density } from "./sessionTypes";
import { ALL_TOGGLEABLE_COLUMNS, COLUMN_LABELS, DENSITY_LABELS } from "./sessionTypes";

interface TableSettingsProps {
  visibleColumns: ColumnKey[];
  density: Density;
  onToggleColumn: (col: ColumnKey) => void;
  onChangeDensity: (density: Density) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

const POP_WIDTH = 280;

export function TableSettings({
  visibleColumns,
  density,
  onToggleColumn,
  onChangeDensity,
  onClose,
  anchorRef,
}: TableSettingsProps) {
  const visibleSet = new Set(visibleColumns);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const compute = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const right = Math.max(16, window.innerWidth - rect.right);
      setCoords({ top: rect.bottom + 6, right });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [onClose, anchorRef]);

  if (!coords) return null;

  const popStyle: React.CSSProperties = {
    position: "fixed",
    top: coords.top,
    right: coords.right,
    background: "#fff",
    border: `1px solid ${PALETTE.line}`,
    borderRadius: 12,
    boxShadow: "0 10px 32px rgba(0,0,0,0.14)",
    padding: "18px 20px 16px",
    width: POP_WIDTH,
    zIndex: 1000,
  };

  return createPortal(
    <div ref={popRef} style={popStyle}>
      <div style={headStyle}>
        <div style={titleStyle}>Table settings</div>
        <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close settings">
          <X size={16} />
        </button>
      </div>

      <div style={sectionLabelStyle}>Edit columns</div>
      <div style={{ ...rowStyle, color: PALETTE.ink4, cursor: "not-allowed" }}>
        <span style={{ ...sqCheckStyle, background: PALETTE.bgFade, borderColor: PALETTE.line, color: PALETTE.ink4 }}>
          <Check size={11} strokeWidth={3} />
        </span>
        Date <span style={alwaysOnStyle}>(always on)</span>
      </div>
      {ALL_TOGGLEABLE_COLUMNS.map((col) => {
        const checked = visibleSet.has(col);
        return (
          <div key={col} style={rowStyle} onClick={() => onToggleColumn(col)}>
            <span style={{ ...sqCheckStyle, ...(checked ? sqCheckCheckedStyle : {}) }}>
              {checked && <Check size={11} strokeWidth={3} />}
            </span>
            {COLUMN_LABELS[col]}
          </div>
        );
      })}
      <div style={{ ...rowStyle, color: PALETTE.ink4, cursor: "not-allowed" }}>
        <span style={{ ...sqCheckStyle, background: PALETTE.bgFade, borderColor: PALETTE.line, color: PALETTE.ink4 }}>
          <Check size={11} strokeWidth={3} />
        </span>
        Payment / Deposit <span style={alwaysOnStyle}>(always on)</span>
      </div>

      <div style={dividerStyle} />

      <div style={sectionLabelStyle}>Display density</div>
      {(["regular", "compact", "ultra"] as Density[]).map((d) => {
        const selected = d === density;
        return (
          <div key={d} style={rowStyle} onClick={() => onChangeDensity(d)}>
            <span style={{ ...radioStyle, ...(selected ? radioSelectedStyle : {}) }}>
              {selected && <span style={radioDotStyle} />}
            </span>
            {DENSITY_LABELS[d]}
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

const headStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 };
const titleStyle: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: PALETTE.ink };
const closeBtnStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.ink3, cursor: "pointer", padding: 2, display: "flex", alignItems: "center" };
const sectionLabelStyle: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: PALETTE.ink2, margin: "10px 0 10px" };
const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "7px 0", fontSize: 14, color: PALETTE.ink, fontWeight: 500, cursor: "pointer" };
const sqCheckStyle: React.CSSProperties = { width: 18, height: 18, border: `1.5px solid ${PALETTE.line}`, borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#fff", flexShrink: 0, color: "transparent" };
const sqCheckCheckedStyle: React.CSSProperties = { background: PALETTE.tealDark, borderColor: PALETTE.tealDark, color: "#fff" };
const alwaysOnStyle: React.CSSProperties = { fontSize: 11, color: PALETTE.ink4 };
const dividerStyle: React.CSSProperties = { height: 1, background: PALETTE.lineSoft, margin: "10px 0 4px" };
const radioStyle: React.CSSProperties = { width: 18, height: 18, border: `1.5px solid ${PALETTE.line}`, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#fff", flexShrink: 0 };
const radioSelectedStyle: React.CSSProperties = { borderColor: PALETTE.tealDark };
const radioDotStyle: React.CSSProperties = { width: 9, height: 9, borderRadius: "50%", background: PALETTE.tealDark };